import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ModuleProvider, useModule, MODULE_COLORS } from "./contexts/ModuleContext";
import { Toaster } from "./components/ui/sonner";
import { MainLayout } from "./components/Layout";
import InstallPWAPrompt from "./components/InstallPWAPrompt";

// Pages - Auth
import AuthPage from "./pages/AuthPage";
import PendingApproval from "./pages/PendingApproval";

// Pages - Arbicsx CFD
import ArbicsxDashboard from "./pages/arbicsx/ArbicsxDashboard";
import ArbicsxCicli from "./pages/arbicsx/ArbicsxCicli";
import ArbicsxWallet from "./pages/arbicsx/ArbicsxWallet";
import ArbicsxProfitti from "./pages/arbicsx/ArbicsxProfitti";
import ArbicsxCostiContiProp from "./pages/arbicsx/ArbicsxCostiContiProp";
import ArbicsxIncassiProp from "./pages/arbicsx/ArbicsxIncassiProp";
import ArbicsxPagamentiPerNoi from "./pages/arbicsx/ArbicsxPagamentiPerNoi";
import ArbicsxPercentualiMensili from "./pages/arbicsx/ArbicsxPercentualiMensili";
import ArbicsxUtenti from "./pages/arbicsx/ArbicsxUtenti";
import ArbicsxImpostazioni from "./pages/arbicsx/ArbicsxImpostazioni";
import ArbicsxMobile from "./pages/arbicsx/ArbicsxMobile";
import ArbicsxFamilySummary from "./pages/arbicsx/ArbicsxFamilySummary";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'eliabonacci92@gmail.com';

// Arbicsx Route - for non-admin users, restricts to Arbicsx CFD only
const FAMILY_VIEW_ALLOWED_PATHS = ['/arbicsx/riepilogo-famiglia', '/arbicsx/impostazioni'];

const ArbicsxRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isImpersonating } = useModule();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  const isAdmin = user?.email === ADMIN_EMAIL || user?.role === 'admin' || user?.role === 'admin_primary' || user?.role === 'admin_secondary';
  const isApproved = user?.is_approved || isAdmin;
  const isViewOnly = user?.view_only === true;
  
  // If user is not approved, show pending page
  if (!isApproved) {
    return <PendingApproval />;
  }
  
  // If route requires admin and user is not admin
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/arbicsx/dashboard" replace />;
  }
  
  const isOnMobilePage = location.pathname === '/mobile';

  // family_view: vista ridotta solo per l'utente loggato (mai in impersonazione, mai per admin)
  const familyView = !isImpersonating && user?.family_view === true && !isAdmin;
  if (familyView && !FAMILY_VIEW_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/arbicsx/riepilogo-famiglia" replace />;
  }
  
  // View Only restrictions: only allow certain pages
  if (isViewOnly && !isAdmin) {
    const allowedPaths = [
      '/arbicsx/dashboard',
      '/arbicsx/profitti',
      '/arbicsx/pagamenti-per-noi',
      '/arbicsx/percentuali-mensili',
      '/arbicsx/impostazioni',
      '/arbicsx/riepilogo-famiglia',
      '/mobile'
    ];
    if (!allowedPaths.includes(location.pathname)) {
      return <Navigate to="/arbicsx/dashboard" replace />;
    }
  }
  
  // Mobile page doesn't need MainLayout
  if (isOnMobilePage) {
    return children;
  }
  
  return <MainLayout>{children}</MainLayout>;
};

// Public Route Component (redirects to appropriate dashboard if authenticated)
// EXCEPTION: If URL has ?ref= parameter, allow access to registration page even if logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const location = useLocation();
  
  // Check if this is a registration invite link
  const searchParams = new URLSearchParams(location.search);
  const hasRefParam = searchParams.has('ref');
  const isRegisterPath = location.pathname === '/register';
  
  // Check if registration was just completed (ref cleared from sessionStorage)
  const registrationCompleted = !sessionStorage.getItem('registration_ref') && hasRefParam;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }
  
  // If user is authenticated and accessing registration with ref param,
  // but registration was NOT just completed, log them out to allow new registration
  // If registration was just completed, redirect normally to dashboard
  if (isAuthenticated && isRegisterPath && hasRefParam && !registrationCompleted) {
    // Check if there's a ref in sessionStorage (meaning user navigated here intentionally)
    const hasRefInSession = sessionStorage.getItem('registration_ref');
    if (hasRefInSession) {
      // Clear token and redirect to same URL (will now render as unauthenticated)
      logout();
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
        </div>
      );
    }
  }
  
  if (isAuthenticated) {
    const isAdmin = user?.email === ADMIN_EMAIL || user?.role === 'admin' || user?.role === 'admin_primary' || user?.role === 'admin_secondary';
    const isApproved = user?.is_approved || isAdmin;
    
    if (!isApproved) {
      return <PendingApproval />;
    }
    
    // Tutti (admin compresi) atterrano sulla dashboard Arbicsx; family_view acceso → Riepilogo famiglia
    if (user?.family_view === true && !isAdmin) {
      return <Navigate to="/arbicsx/riepilogo-famiglia" replace />;
    }
    return <Navigate to="/arbicsx/dashboard" replace />;
  }
  
  return children;
};

// Component to apply CSS variables based on active module
const ThemeProvider = ({ children }) => {
  const { activeModule } = useModule();
  
  useEffect(() => {
    const colors = MODULE_COLORS[activeModule];
    if (colors) {
      // Update custom accent variables
      document.documentElement.style.setProperty('--accent-primary', colors.primary);
      document.documentElement.style.setProperty('--accent-primary-hover', colors.primaryHover);
      document.documentElement.style.setProperty('--accent-primary-light', colors.primaryLight);
      document.documentElement.style.setProperty('--accent-primary-border', colors.primaryBorder);
      document.documentElement.style.setProperty('--accent-text', colors.text);
      
      // Update Tailwind's --primary variable (used by bg-primary, text-primary, etc.)
      document.documentElement.style.setProperty('--primary', colors.primaryHsl);
      document.documentElement.style.setProperty('--ring', colors.primaryHsl);
    }
  }, [activeModule]);
  
  return children;
};

function AppRoutes() {
  return (
    <ThemeProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><AuthPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><AuthPage /></PublicRoute>} />
        
        {/* Arbitex Protected Routes - Admin only */}
        {/* Rotte legacy Arbitex / AntiFutures (UI rimossa): redirect alla dashboard Arbicsx */}
        <Route path="/dashboard" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/cicli" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/profitti" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/costi-conti-prop" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/payouts" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/rimborsi" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/pagamenti-per-noi" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/pagamenti-arbitex" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/percentuali-mensili" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/sandy" element={<Navigate to="/arbicsx/dashboard" replace />} />
        <Route path="/impostazioni" element={<Navigate to="/arbicsx/dashboard" replace />} />
        
        {/* Arbicsx CFD Routes - All approved users */}
        <Route path="/arbicsx/dashboard" element={<ArbicsxRoute><ArbicsxDashboard /></ArbicsxRoute>} />
        <Route path="/arbicsx/cicli" element={<ArbicsxRoute><ArbicsxCicli /></ArbicsxRoute>} />
        <Route path="/arbicsx/wallet" element={<ArbicsxRoute><ArbicsxWallet /></ArbicsxRoute>} />
        <Route path="/arbicsx/profitti" element={<ArbicsxRoute><ArbicsxProfitti /></ArbicsxRoute>} />
        <Route path="/arbicsx/costi-conti-prop" element={<ArbicsxRoute><ArbicsxCostiContiProp /></ArbicsxRoute>} />
        <Route path="/arbicsx/incassi-prop" element={<ArbicsxRoute><ArbicsxIncassiProp /></ArbicsxRoute>} />
        <Route path="/arbicsx/pagamenti-per-noi" element={<ArbicsxRoute><ArbicsxPagamentiPerNoi /></ArbicsxRoute>} />
        <Route path="/arbicsx/percentuali-mensili" element={<ArbicsxRoute><ArbicsxPercentualiMensili /></ArbicsxRoute>} />
        <Route path="/arbicsx/utenti" element={<ArbicsxRoute requireAdmin={true}><ArbicsxUtenti /></ArbicsxRoute>} />
        <Route path="/arbicsx/impostazioni" element={<ArbicsxRoute><ArbicsxImpostazioni /></ArbicsxRoute>} />
        <Route path="/arbicsx/riepilogo-famiglia" element={<ArbicsxRoute><ArbicsxFamilySummary /></ArbicsxRoute>} />
        
        {/* Mobile View - Optimized for smartphones */}
        <Route path="/mobile" element={<ArbicsxRoute><ArbicsxMobile /></ArbicsxRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModuleProvider>
          <AppRoutes />
          <InstallPWAPrompt />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#111827',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }
            }}
          />
        </ModuleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
